import * as React from 'react';

import dataTableCsv from 'components/ReactDataTable/ReactDataTableCsv';
import useWindowSize from 'hooks/useWindowSize';
import DataTable, { TableColumn } from 'react-data-table-component';

import styles from './DataDashboard.module.css';
import { totalFlagsForReview } from './dataquality.utils';

const getData = (
  panelReviews: dataquality.PanelQualityData[],
  latestAppBuild: string,
): TableRow[] =>
  Array.from(new Set(panelReviews.map((d) => d.userId))).map((userId) => {
    const userPanelReviews = panelReviews.filter((d) => d.userId == userId);

    const rowInfo: TableRow = {
      id: userPanelReviews[0].userId,
      email: userPanelReviews[0].email || '—',
      userId: userPanelReviews[0].userId,
      username: userPanelReviews[0].username?.trim() || null,
    };

    userPanelReviews.map((review) => {
      const runningFlagTotal = totalFlagsForReview(review);

      let columnString = runningFlagTotal.toString();

      if (latestAppBuild && review.appRevision !== latestAppBuild) {
        columnString += '\t❗';
      }

      let columnObject = {};

      if (review.numggvar) {
        columnObject = {
          columnString: columnString,
          pq: review.perceivedQuality,
          noGgVar: review.numggvar.toString(),
        };
      } else {
        columnObject = {
          columnString: columnString,
          pq: review.perceivedQuality,
        };
      }

      rowInfo[review.productName] = columnObject;
    });

    return rowInfo;
  });

interface Props {
  panelProducts: {
    productByProductId: {
      name: string;
    };
  }[];
  panelReviews: dataquality.PanelQualityData[];
  latestAppBuild?: string;
  hideEmails?: boolean;
}

interface TableRow {
  id: number | string;
  email: string;
  userId: number;
  username?: string | null;
  [key: string]: any;
}

const UserProductDataQualityTable: React.FC<Props> = ({
  panelProducts,
  panelReviews,
  latestAppBuild,
  hideEmails,
}) => {
  const { width } = useWindowSize();

  const colWidth = (windowWidth: number) =>
    (windowWidth - 250) / (panelProducts.length + 2);

  const columns: TableColumn<TableRow>[] = ([
    {
      id: 'email',
      selector: (row: TableRow) => row.email,
      name: 'Email',
      sortable: true,
      wrap: true,
      omit: hideEmails,
      width: `${colWidth(width)}px`,
      cell: (row: TableRow) => (
        <div style={{ textAlign: row.email === '—' ? 'center' : 'left', width: '100%' }}>
          {row.email}
        </div>
      ),
    },
    {
      id: 'userId',
      selector: (row: TableRow) => row.userId,
      name: 'User Name / ID',
      sortable: true,
      wrap: true,
      maxWidth: `${colWidth(width)}px`,
      cell: (row: TableRow) => (
        <div style={{ textAlign: row.userId ? 'left' : 'center', width: '100%' }}>
          {row.username || row.userId || '—'}
        </div>
      ),
    },
    {
      id: 'username',
      selector: (row: TableRow) => row.username,
      name: 'Username',
      omit: true,
    },
  ] as TableColumn<TableRow>[]).concat(
    panelProducts.map((product) => ({
      id: product.productByProductId.name,
      selector: (row: TableRow) => row[product.productByProductId.name]?.pq,
      name: product.productByProductId.name,
      sortable: true,
      wrap: true,
      width: `${colWidth(width)}px`,
      cell: (row: TableRow) => {
        if (row[product.productByProductId.name] != undefined) {
          return (
            <div>
              {row[product.productByProductId.name].columnString && (
                <span>{row[product.productByProductId.name].columnString}</span>
              )}
              <br />
              {row[product.productByProductId.name].noGgVar && (
                <span>
                  {'No. of GGVars marked: ' +
                    row[product.productByProductId.name].noGgVar}
                </span>
              )}
              <br />
              {row[product.productByProductId.name].pq && (
                <span>{'PQ: ' + row[product.productByProductId.name].pq}</span>
              )}
            </div>
          );
        } else {
          return <div />;
        }
      },
    })),
  );

  return (
    <div>
      <DataTable
        customStyles={{
          responsiveWrapper: {
            style: {
              maxHeight: 'none',
            },
          },
        }}
        columns={columns}
        data={getData(panelReviews, latestAppBuild)}
        fixedHeader
        striped
        actions={dataTableCsv(
          'User_Product_Data_Quality.csv',
          columns.filter((col) => !hideEmails || col.id !== 'email'),
          getData(panelReviews, latestAppBuild).map((row) => {
            const lcObj: Record<string, string> = {};
            if (!hideEmails) {
              lcObj.email = row.email;
            }
            lcObj.userId = row.userId?.toString();
            lcObj.username = row.username || '';
            columns.slice(3).forEach((column) => {
              if (row[column.id]) {
                lcObj[column.id] = `${row[column.id].columnString || ''}, No. of GGVars marked: ${row[column.id].noGgVar || ''}, PQ: ${row[column.id].pq || ''}`;
              }
            });
            return lcObj;
          }) as unknown as TableRow[],
        )}
      />
      <span className={styles.refreshMsg}>
        Reviews with ❗ are completed using an older build of the app.
      </span>
    </div>
  );
};

export default UserProductDataQualityTable;
